/**
 * Champs de date au format français, indépendants de la langue du navigateur
 * (les champs natifs `type="date"` affichent mm/dd/yyyy et AM/PM sur un
 * navigateur anglais). Chacun reçoit et renvoie la même valeur technique que
 * son équivalent natif, pour ne rien changer aux formulaires ni à l'API :
 *
 * - `DateInput` : « jj/mm/aaaa » ↔ `YYYY-MM-DD`, avec calendrier ;
 * - `TimeInput` : « hh:mm » (24 h) ↔ `HH:mm` ;
 * - `MonthInput` : « mm/aaaa » ↔ `YYYY-MM`, avec sélecteur de mois ;
 * - `DateTimeInput` : « jj/mm/aaaa hh:mm » ↔ `YYYY-MM-DDTHH:mm`, avec calendrier.
 *
 * Tant que le texte n'est pas une date complète et valide, la valeur vaut `''`,
 * comme un champ natif incomplet. Les séparateurs s'insèrent à la frappe ;
 * « 5/11/26 » est accepté puis réécrit « 05/11/2026 » à la sortie du champ.
 */
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  FRENCH_MONTHS,
  FRENCH_MONTHS_SHORT,
  FRENCH_WEEKDAYS,
  FRENCH_WEEKDAYS_SHORT,
  calendarDays,
  formatDateText,
  formatDateTimeText,
  formatDayLabel,
  formatMonthLabel,
  formatMonthText,
  formatTimeText,
  maskDateText,
  maskDateTimeText,
  maskMonthText,
  maskTimeText,
  parisTodayDate,
  parseDateText,
  parseDateTimeText,
  parseMonthText,
  parseTimeText,
  shiftDay,
  shiftMonth,
} from '@/lib/date-input';
import { cn } from '@/lib/utils';

type NativeInputProps = Omit<
  React.ComponentProps<'input'>,
  'value' | 'defaultValue' | 'onChange' | 'type' | 'ref' | 'size'
>;

export interface DateLikeInputProps extends NativeInputProps {
  /** Valeur technique (`YYYY-MM-DD`, `HH:mm`, `YYYY-MM` ou `YYYY-MM-DDTHH:mm`), `''` si vide. */
  value: string;
  onChange: (value: string) => void;
  controlSize?: 'default' | 'sm';
}

interface TextSpec {
  mask: (raw: string) => string;
  parse: (text: string) => string | null;
  format: (value: string) => string;
  placeholder: string;
  /** Message de validation natif quand le texte saisi n'est pas une date complète. */
  invalid: string;
}

const DATE_SPEC: TextSpec = {
  mask: maskDateText,
  parse: parseDateText,
  format: formatDateText,
  placeholder: 'jj/mm/aaaa',
  invalid: 'Date incomplète ou invalide (jj/mm/aaaa).',
};
const TIME_SPEC: TextSpec = {
  mask: maskTimeText,
  parse: parseTimeText,
  format: formatTimeText,
  placeholder: 'hh:mm',
  invalid: 'Heure incomplète ou invalide (hh:mm).',
};
const MONTH_SPEC: TextSpec = {
  mask: maskMonthText,
  parse: parseMonthText,
  format: formatMonthText,
  placeholder: 'mm/aaaa',
  invalid: 'Mois incomplet ou invalide (mm/aaaa).',
};
const DATE_TIME_SPEC: TextSpec = {
  mask: maskDateTimeText,
  parse: parseDateTimeText,
  format: formatDateTimeText,
  placeholder: 'jj/mm/aaaa hh:mm',
  invalid: 'Date incomplète ou invalide (jj/mm/aaaa hh:mm).',
};

/**
 * Texte affiché et valeur émise. Le texte suit la valeur reçue quand elle
 * change de l'extérieur (raccourci « lundi prochain », réinitialisation).
 */
function useMaskedText(
  spec: TextSpec,
  value: string,
  onChange: (value: string) => void,
): {
  text: string;
  type: (raw: string) => void;
  normalize: () => void;
  pick: (value: string) => void;
} {
  const [text, setText] = React.useState(() => spec.format(value));
  const [known, setKnown] = React.useState(value);
  if (value !== known) {
    setKnown(value);
    setText(spec.format(value));
  }
  const emit = (next: string): void => {
    setKnown(next);
    if (next !== value) {
      onChange(next);
    }
  };
  return {
    text,
    type: (raw) => {
      const masked = spec.mask(raw);
      setText(masked);
      emit(spec.parse(masked) ?? '');
    },
    normalize: () => {
      const parsed = spec.parse(text);
      if (parsed !== null) {
        setText(spec.format(parsed));
      }
    },
    pick: (next) => {
      setText(spec.format(next));
      emit(next);
    },
  };
}

interface MaskedInputProps extends DateLikeInputProps {
  spec: TextSpec;
  picker?: (props: {
    value: string;
    pick: (value: string) => void;
    close: () => void;
  }) => React.ReactNode;
  pickerLabel?: string;
}

function MaskedInput({
  spec,
  picker,
  pickerLabel,
  value,
  onChange,
  onBlur,
  className,
  controlSize,
  disabled,
  placeholder,
  ...props
}: MaskedInputProps): React.JSX.Element {
  const masked = useMaskedText(spec, value, onChange);
  const [open, setOpen] = React.useState(false);
  const field = React.useRef<HTMLInputElement>(null);
  // Un texte commencé mais incomplet bloque l'envoi natif du formulaire, avec un message français.
  const incomplete = masked.text.trim() !== '' && spec.parse(masked.text) === null;
  React.useEffect(() => {
    field.current?.setCustomValidity(incomplete ? spec.invalid : '');
  }, [incomplete, spec]);
  const input = (
    <Input
      {...props}
      ref={field}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      controlSize={controlSize}
      disabled={disabled}
      placeholder={placeholder ?? spec.placeholder}
      className={cn('font-mono tabular-nums', picker !== undefined && 'pr-9', className)}
      value={masked.text}
      onChange={(event) => {
        masked.type(event.target.value);
      }}
      onBlur={(event) => {
        masked.normalize();
        onBlur?.(event);
      }}
    />
  );
  if (picker === undefined) {
    return input;
  }
  // Le calendrier s'ouvre sous le champ entier, aligné sur son bord gauche.
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative w-full min-w-0">
          {input}
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              disabled={disabled}
              aria-label={pickerLabel}
              className={cn(
                'absolute right-0.5 top-1/2 -translate-y-1/2 text-muted-foreground',
                controlSize === 'sm' && 'h-6 w-6',
              )}
            >
              <CalendarDays />
            </Button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
      >
        {picker({
          value,
          pick: masked.pick,
          close: () => {
            setOpen(false);
          },
        })}
      </PopoverContent>
    </Popover>
  );
}

/** Date « jj/mm/aaaa », valeur `YYYY-MM-DD`. */
export function DateInput(props: DateLikeInputProps): React.JSX.Element {
  return (
    <MaskedInput
      {...props}
      spec={DATE_SPEC}
      pickerLabel="Choisir une date"
      picker={({ value, pick, close }) => (
        <Calendar
          value={value}
          onSelect={(date) => {
            pick(date);
            close();
          }}
        />
      )}
    />
  );
}

/** Heure « hh:mm » sur 24 heures, valeur `HH:mm`. */
export function TimeInput(props: DateLikeInputProps): React.JSX.Element {
  return <MaskedInput {...props} spec={TIME_SPEC} />;
}

/** Mois « mm/aaaa », valeur `YYYY-MM`. */
export function MonthInput(props: DateLikeInputProps): React.JSX.Element {
  return (
    <MaskedInput
      {...props}
      spec={MONTH_SPEC}
      pickerLabel="Choisir un mois"
      picker={({ value, pick, close }) => (
        <MonthPicker
          value={value}
          onSelect={(month) => {
            pick(month);
            close();
          }}
        />
      )}
    />
  );
}

/** Date et heure « jj/mm/aaaa hh:mm », valeur `YYYY-MM-DDTHH:mm` (celle d'un `datetime-local`). */
export function DateTimeInput(props: DateLikeInputProps): React.JSX.Element {
  return (
    <MaskedInput
      {...props}
      spec={DATE_TIME_SPEC}
      pickerLabel="Choisir une date"
      picker={({ value, pick, close }) => (
        <Calendar
          value={value.slice(0, 10)}
          onSelect={(date) => {
            // Le calendrier change le jour et garde l'heure déjà saisie (minuit sinon).
            pick(`${date}T${value.slice(11, 16) || '00:00'}`);
            close();
          }}
        />
      )}
    />
  );
}

// --- Calendrier ---------------------------------------------------------------

const DAY_KEYS: Readonly<Record<string, (date: string) => string>> = {
  ArrowLeft: (date) => shiftDay(date, -1),
  ArrowRight: (date) => shiftDay(date, 1),
  ArrowUp: (date) => shiftDay(date, -7),
  ArrowDown: (date) => shiftDay(date, 7),
  PageUp: (date) => shiftMonthKeepingDay(date, -1),
  PageDown: (date) => shiftMonthKeepingDay(date, 1),
  Home: (date) => shiftDay(date, -weekdayIndex(date)),
  End: (date) => shiftDay(date, 6 - weekdayIndex(date)),
};

function weekdayIndex(date: string): number {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
}

/** Même jour du mois voisin, ramené au dernier jour si besoin (31 → 30). */
function shiftMonthKeepingDay(date: string, delta: number): string {
  const month = shiftMonth(date.slice(0, 7), delta);
  const day = Number(date.slice(8, 10));
  const days = calendarDays(month).filter((d) => d.startsWith(month));
  return days[Math.min(day, days.length) - 1] ?? `${month}-01`;
}

const isDate = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

/** Calendrier mensuel en français, semaine du lundi au dimanche. Flèches, Page↑/↓, Début/Fin. */
export function Calendar({
  value,
  onSelect,
}: {
  value: string;
  onSelect: (date: string) => void;
}): React.JSX.Element {
  const today = parisTodayDate();
  const selected = isDate(value) ? value : null;
  const [focused, setFocused] = React.useState(selected ?? today);
  const [view, setView] = React.useState(focused.slice(0, 7));
  const grid = React.useRef<HTMLDivElement>(null);
  const shouldFocus = React.useRef(true);

  React.useEffect(() => {
    if (shouldFocus.current) {
      shouldFocus.current = false;
      grid.current?.querySelector<HTMLButtonElement>(`[data-date="${focused}"]`)?.focus();
    }
  }, [focused, view]);

  const days = calendarDays(view);
  const tabStop = focused.startsWith(view) ? focused : `${view}-01`;

  return (
    <div className="w-[16.5rem] select-none">
      <div className="mb-1 flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Mois précédent"
          onClick={() => {
            setView(shiftMonth(view, -1));
          }}
        >
          <ChevronLeft />
        </Button>
        <span className="text-sm font-medium first-letter:uppercase" aria-live="polite">
          {formatMonthLabel(view)}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Mois suivant"
          onClick={() => {
            setView(shiftMonth(view, 1));
          }}
        >
          <ChevronRight />
        </Button>
      </div>
      <div className="grid grid-cols-7 text-center text-caption text-muted-foreground">
        {FRENCH_WEEKDAYS_SHORT.map((label, index) => (
          <abbr key={label} title={FRENCH_WEEKDAYS[index]} className="py-1 no-underline">
            {label}
          </abbr>
        ))}
      </div>
      <div
        ref={grid}
        role="group"
        aria-label={formatMonthLabel(view)}
        className="grid grid-cols-7 gap-0.5"
        onKeyDown={(event) => {
          const move = DAY_KEYS[event.key];
          if (move === undefined) {
            return;
          }
          event.preventDefault();
          const next = move(tabStop);
          shouldFocus.current = true;
          setFocused(next);
          setView(next.slice(0, 7));
        }}
      >
        {days.map((day) => {
          const outside = !day.startsWith(view);
          const isSelected = day === selected;
          return (
            <button
              key={day}
              type="button"
              data-date={day}
              tabIndex={day === tabStop ? 0 : -1}
              aria-label={formatDayLabel(day)}
              aria-pressed={isSelected}
              aria-current={day === today ? 'date' : undefined}
              onClick={() => {
                onSelect(day);
              }}
              className={cn(
                'h-8 rounded-md text-sm tabular-nums transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                outside && 'text-muted-foreground/60',
                day === today && !isSelected && 'font-semibold text-primary',
                isSelected &&
                  'bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground',
              )}
            >
              {Number(day.slice(8, 10))}
            </button>
          );
        })}
      </div>
      <div className="mt-1 flex justify-end border-t pt-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            onSelect(today);
          }}
        >
          Aujourd’hui
        </Button>
      </div>
    </div>
  );
}

/** Sélecteur de mois : douze mois de l'année affichée, années précédente et suivante. */
function MonthPicker({
  value,
  onSelect,
}: {
  value: string;
  onSelect: (month: string) => void;
}): React.JSX.Element {
  const current = parisTodayDate().slice(0, 7);
  const selected = /^\d{4}-\d{2}$/.test(value) ? value : null;
  const [year, setYear] = React.useState(Number((selected ?? current).slice(0, 4)));
  const first = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    first.current?.focus();
  }, []);

  return (
    <div className="w-56 select-none">
      <div className="mb-1 flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Année précédente"
          onClick={() => {
            setYear(year - 1);
          }}
        >
          <ChevronLeft />
        </Button>
        <span className="text-sm font-medium tabular-nums" aria-live="polite">
          {year}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Année suivante"
          onClick={() => {
            setYear(year + 1);
          }}
        >
          <ChevronRight />
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {FRENCH_MONTHS_SHORT.map((label, index) => {
          const month = `${year}-${String(index + 1).padStart(2, '0')}`;
          const isSelected = month === selected;
          const focusTarget = month === (selected ?? current);
          return (
            <button
              key={month}
              ref={focusTarget ? first : undefined}
              type="button"
              aria-label={`${FRENCH_MONTHS[index] ?? label} ${year}`}
              aria-pressed={isSelected}
              aria-current={month === current ? 'date' : undefined}
              onClick={() => {
                onSelect(month);
              }}
              className={cn(
                'h-8 rounded-md text-sm transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                month === current && !isSelected && 'font-semibold text-primary',
                isSelected &&
                  'bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground',
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
